import { useCallback, useRef, useState } from "react";
import type { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { OverlayPanel } from "primereact/overlaypanel";

import { Paths } from "@/core/constants/paths";
import { clearUserPhotoCache, useUserPhoto } from "@/hooks";
import { getPersistedUser, logoutFromBff } from "@/modules/auth";

import {
  NAVIGATOR_USER_FALLBACK_NAME,
  NAVIGATOR_USER_PHOTO_PATH,
} from "./navigator-user.constants";
import type { NavigatorUserProps } from "./types-navigator-user";

/**
 * ViewModel do rodapé de usuário do Navigator e do `user-profile-modal` (Figma 5037-105413 e 5037-105894): dados do
 * usuário autenticado, foto, abertura do painel e saída da sessão.
 */
export function useNavigatorUser({ variant, testId }: NavigatorUserProps) {
  const navigate = useNavigate();
  const panelRef = useRef<OverlayPanel>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const user = getPersistedUser();
  const name = user?.name?.trim() ?? "";
  const displayName = name || NAVIGATOR_USER_FALLBACK_NAME;
  const photoSrc = useUserPhoto(NAVIGATOR_USER_PHOTO_PATH);

  const togglePanel = useCallback((event: MouseEvent<HTMLElement>) => {
    panelRef.current?.toggle(event);
  }, []);

  async function logout() {
    setIsLoggingOut(true);

    try {
      await logoutFromBff();
    } finally {
      clearUserPhotoCache();
      navigate(Paths.LOGIN, {
        replace: true,
        state: {
          logoutMessage: {
            code: "MS001",
            severity: "success",
            summary: "Sucesso",
            detail: "Sua sessão foi encerrada com sucesso.",
          },
        },
      });
    }
  }

  return {
    variant,
    showDetails: variant === "full",
    displayName,
    area: user?.area?.trim() || null,
    jobTitle: user?.jobTitle?.trim() || null,
    email: user?.email?.trim() || null,
    name,
    photoSrc,
    triggerLabel: `Perfil de ${displayName}`,
    panelRef,
    panelOpen,
    togglePanel,
    onPanelShow: () => setPanelOpen(true),
    onPanelHide: () => setPanelOpen(false),
    isLoggingOut,
    logout,
    testIds: {
      button: `${testId}-button`,
      panel: `${testId}-panel`,
      accessibility: `${testId}-accessibility`,
      logout: `${testId}-logout`,
    },
  };
}
